
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrganizationMembersWithRole } from "@/hooks/useOrganizationMembersWithRole";
import { useMemberOperations } from "@/components/organization/members/hooks/useMemberOperations";
import { AddMemberForm } from "@/components/organization/members/forms/AddMemberForm";
import { DeleteMemberDialog } from "@/components/organization/members/dialogs/DeleteMemberDialog";
import type { Database } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Mail, Trash2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type OrganizationMember = Database['public']['Tables']['organization_members']['Row'];

const OrganizationUsers = () => {
  const { organizationName } = useParams();
  const [memberToDelete, setMemberToDelete] = useState<OrganizationMember | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { organizationId, isLoading: orgLoading } = useOrganization();
  const { members, loading: membersLoading, refetchMembers, isAdmin } = useOrganizationMembersWithRole(organizationId);
  const { 
    isAdding,
    updatingRole,
    removingMember,
    resendingEmail,
    handleAddMember,
    handleUpdateRole,
    handleDeleteMember,
    handleResendInvitation
  } = useMemberOperations({
    organizationId,
    onSuccess: refetchMembers
  });

  const handleConfirmDelete = async () => {
    if (memberToDelete) {
      await handleDeleteMember(memberToDelete.id);
      setIsDeleteDialogOpen(false);
      setMemberToDelete(null);
    }
  };

  // Filter members based on search term
  const filteredMembers = members.filter(member => 
    member.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper function to get status display info
  const getStatusInfo = (member: OrganizationMember) => {
    const status = member.invitation_status || 'pending';
    
    switch (status) {
      case 'active':
        return { label: 'Active', variant: 'default' as const, color: 'bg-green-100 text-green-800 border-green-200' };
      case 'sent':
        return { label: 'Invitation Sent', variant: 'secondary' as const, color: 'bg-blue-100 text-blue-800 border-blue-200' };
      case 'expired':
        return { label: 'Invitation Expired', variant: 'destructive' as const, color: 'bg-red-100 text-red-800 border-red-200' };
      case 'accepted':
        return { label: 'Invitation Accepted', variant: 'default' as const, color: 'bg-green-100 text-green-800 border-green-200' };
      case 'pending':
      default:
        return { label: 'Invitation Pending', variant: 'outline' as const, color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    }
  };

  // Helper function to determine if resend button should be shown
  const shouldShowResendButton = (member: OrganizationMember) => {
    const status = member.invitation_status || 'pending';
    return ['pending', 'sent', 'expired'].includes(status);
  };

  if (orgLoading || membersLoading || !organizationId) {
    return (
      <div className="relative flex size-full min-h-screen flex-col bg-white">
        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="flex items-center justify-center min-h-[50vh]">
            <p className="text-[#637588] text-sm font-normal leading-normal">Loading organization details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex size-full min-h-screen flex-col bg-white" style={{ fontFamily: 'Newsreader, "Noto Sans", sans-serif' }}>
      <div className="layout-container flex h-full grow flex-col">
        <div className="px-40 flex flex-1 justify-center py-5">
          <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
            <div className="flex flex-wrap justify-between gap-3 p-4">
              <p className="text-[#111418] tracking-light text-[32px] font-bold leading-tight min-w-72">
                User Management
              </p>
              <Button
                onClick={() => setShowCreateForm(!showCreateForm)}
                disabled={!organizationId || orgLoading}
                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-8 px-4 bg-[#f0f2f4] text-[#111418] text-sm font-medium leading-normal hover:bg-[#e8eaed] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="truncate">{showCreateForm ? 'Cancel' : 'Add User'}</span>
              </Button>
            </div>

            {showCreateForm && (
              <div className="px-4 pb-4">
                <div className="bg-white border border-[#dce0e5] rounded-lg p-4">
                  {!organizationId ? (
                    <div className="flex items-center justify-center p-4">
                      <p className="text-[#637588] text-sm font-normal leading-normal">Loading organization data...</p>
                    </div>
                  ) : (
                    <AddMemberForm 
                      onAddMember={async (email, fullName, role) => {
                        const success = await handleAddMember(email, fullName, role);
                        if (success) {
                          setShowCreateForm(false);
                        }
                        return success;
                      }}
                      isAdding={isAdding}
                    />
                  )}
                </div>
              </div>
            )}

            <div className="px-4 py-3">
              <label className="flex flex-col min-w-40 h-12 w-full">
                <div className="flex w-full flex-1 items-stretch rounded-lg h-full">
                  <div className="text-[#637588] flex border-none bg-[#f0f2f4] items-center justify-center pl-4 rounded-l-lg border-r-0">
                    <Search size={24} />
                  </div>
                  <Input
                    placeholder="Search users"
                    className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111418] focus:outline-0 focus:ring-0 border-none bg-[#f0f2f4] focus:border-none h-full placeholder:text-[#637588] px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </label>
            </div>

            <div className="px-4 py-3">
              <div className="flex overflow-hidden rounded-lg border border-[#dce0e5] bg-white">
                <table className="flex-1">
                  <thead>
                    <tr className="bg-white">
                      <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">Name</th>
                      {isAdmin && <th className="px-4 py-3 text-left text-[#111418] w-[400px] text-sm font-medium leading-normal">Email</th>}
                      <th className="px-4 py-3 text-left text-[#111418] w-60 text-sm font-medium leading-normal">Role</th>
                      <th className="px-4 py-3 text-left text-[#111418] w-60 text-sm font-medium leading-normal">Status</th>
                      <th className="px-4 py-3 text-left text-[#111418] w-60 text-[#637588] text-sm font-medium leading-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 5 : 4} className="h-[72px] px-4 py-2 text-center text-[#637588] text-sm font-normal leading-normal">
                          {searchTerm ? 'No users found matching your search' : 'No members found in this organization'}
                        </td>
                      </tr>
                    ) : (
                      filteredMembers.map((member) => (
                        <tr key={member.id} className="border-t border-t-[#dce0e5]">
                          <td className="h-[72px] px-4 py-2 w-[400px] text-[#111418] text-sm font-normal leading-normal">
                            {member.full_name}
                          </td>
                          {isAdmin && (
                            <td className="h-[72px] px-4 py-2 w-[400px] text-[#637588] text-sm font-normal leading-normal">
                              {member.email}
                            </td>
                          )}
                          <td className="h-[72px] px-4 py-2 w-60 text-sm font-normal leading-normal">
                            <Select
                              value={member.role}
                              onValueChange={(newRole) => handleUpdateRole(member.id, newRole)}
                              disabled={updatingRole === member.id}
                            >
                              <SelectTrigger className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-8 px-4 bg-[#f0f2f4] text-[#111418] text-sm font-medium leading-normal w-full border-none">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="MEMBER">Member</SelectItem>
                                <SelectItem value="ADMIN">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="h-[72px] px-4 py-2 w-60 text-sm font-normal leading-normal">
                            <div className="flex justify-center">
                              <Badge className={`${getStatusInfo(member).color} border`}>
                                {getStatusInfo(member).label}
                              </Badge>
                            </div>
                          </td>
                          <td className="h-[72px] px-4 py-2 w-60 text-[#637588] text-sm font-bold leading-normal tracking-[0.015em]">
                            <div className="flex items-center gap-2">
                              {handleResendInvitation && shouldShowResendButton(member) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResendInvitation(member.id)}
                                  disabled={resendingEmail === member.id}
                                  className="h-8 w-8 p-0 hover:bg-[#f0f2f4]"
                                  title={member.invitation_status === 'pending' ? 'Send invitation' : 'Resend invitation'}
                                >
                                  <Mail className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setMemberToDelete(member);
                                  setIsDeleteDialogOpen(true);
                                }}
                                disabled={removingMember === member.id}
                                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
                                title="Remove member"
                              >
                                {removingMember === member.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <DeleteMemberDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          memberToDelete={memberToDelete}
          onConfirm={handleConfirmDelete}
          isLoading={removingMember === memberToDelete?.id}
        />
      </div>
    </div>
  );
};

export default OrganizationUsers;
